import { Edit, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditExpectedDateMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Input, useToast } from "randmarcomps";
import withManufacturer from "../../../contexts/WithManufacturer";
import { getCurrentDate } from "../../util/DateTimeUtil";

interface IProps {
  purchaseOrderNumber: string;
  expectedArrivalDate: number;
  applicationId: string;
}

const EditExpectedDate: React.FC<IProps> = ({ purchaseOrderNumber, expectedArrivalDate, applicationId }) => {
  const formatDate = (dateNum: number) => {
    const dateStr = dateNum.toString();
    if (dateStr.length < 8) return "";
    return `${dateStr.substr(0, 4)}-${dateStr.substr(4, 2)}-${dateStr.substr(6, 2)}`;
  };

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(formatDate(expectedArrivalDate));
  const { toast } = useToast();
  const [editExpectedDate, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditExpectedDateMutation();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDateChange = (newDate: string) => {
    const nd: number = +newDate.split("-").join("");
    if (nd < getCurrentDate()) return;
    setDate(newDate);
  };

  const handleConfirm = async () => {
    try {
      await editExpectedDate({
        routeManufacturerId: applicationId,
        purchaseOrderNumber,
        newValue: date.split("-").join("")
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Expected date changed for ${purchaseOrderNumber}.` });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Could not change expected date for ${purchaseOrderNumber}` });
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && setOpen(o)}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Edit expected arrival date" className="ml-1">
          <Edit className="h-4 w-4 text-blue-600" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle>Edit Expected Date</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-4 text-lg">Please wait...</span>
          </div>
        ) : (
          <>
            <div className="p-4">
              <Input
                id="PO.EditExpectedDate.DatePicker"
                type="date"
                value={date}
                onChange={e => handleDateChange(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirm}>
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withManufacturer(EditExpectedDate);
